import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DollarSign,
  Building2,
  TrendingUp,
  TrendingDown,
  Percent,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Download,
} from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchCostDashboard,
  selectCostDashboard,
  selectCostsLoading,
  selectCostsError,
  clearError,
} from '@/store/slices/costsSlice';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassPageHeader,
  GlassLoadingSpinner,
  SectionReveal,
  MetricCard,
  ProgressBar,
  CollapsibleMetricsSection,
} from '@/design-system';
import { CostCategory, UserRole } from '@/types';
import type { DivisionCostSummary, ProgramCostSummary } from '@/types';
import { formatCurrencyCompact, formatPercent } from '@/utils/formatters';

const getCategoryLabel = (category: CostCategory): string => {
  const labels: Record<CostCategory, string> = {
    [CostCategory.LABOR]: 'Labor',
    [CostCategory.SOFTWARE_LICENSES]: 'Software',
    [CostCategory.INFRASTRUCTURE]: 'Infrastructure',
    [CostCategory.TRAINING]: 'Training',
    [CostCategory.TRAVEL]: 'Travel',
    [CostCategory.CONTRACTOR_SERVICES]: 'Contractors',
    [CostCategory.OTHER]: 'Other',
  };
  return labels[category] || category;
};

const getCategoryColor = (category: CostCategory): string => {
  const colors: Record<CostCategory, string> = {
    [CostCategory.LABOR]: 'bg-blue-500',
    [CostCategory.SOFTWARE_LICENSES]: 'bg-violet-500',
    [CostCategory.INFRASTRUCTURE]: 'bg-cyan-500',
    [CostCategory.TRAINING]: 'bg-emerald-500',
    [CostCategory.TRAVEL]: 'bg-amber-500',
    [CostCategory.CONTRACTOR_SERVICES]: 'bg-rose-500',
    [CostCategory.OTHER]: 'bg-gray-500',
  };
  return colors[category] || 'bg-gray-500';
};

const CostDashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const dashboardData = useSelector(selectCostDashboard);
  const loading = useSelector(selectCostsLoading);
  const error = useSelector(selectCostsError);
  const { user } = useSelector((state: RootState) => state.auth);

  const [expandedDivisions, setExpandedDivisions] = useState<Set<string>>(new Set());

  // Check permission
  const canView =
    user?.role === UserRole.ADMIN ||
    user?.role === UserRole.EXECUTIVE ||
    user?.role === UserRole.FINANCE;

  useEffect(() => {
    if (canView) {
      dispatch(fetchCostDashboard());
    }
  }, [dispatch, canView]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error, dispatch]);

  const toggleDivision = (divisionId: string) => {
    const newExpanded = new Set(expandedDivisions);
    if (newExpanded.has(divisionId)) {
      newExpanded.delete(divisionId);
    } else {
      newExpanded.add(divisionId);
    }
    setExpandedDivisions(newExpanded);
  };

  const handleRefresh = () => {
    dispatch(fetchCostDashboard());
  };

  const handleExportCSV = () => {
    if (!dashboardData) return;

    const csvRows: string[][] = [
      ['Cost Dashboard Report'],
      [`Generated: ${new Date().toLocaleString()}`],
      [],
      ['SUMMARY'],
      ['Total Costs', (dashboardData.totalCosts ?? 0).toString()],
      ['Total Budget', (dashboardData.totalBudget ?? 0).toString()],
      ['Utilization %', (dashboardData.utilizationPercent ?? 0).toString()],
      [],
      ['COSTS BY CATEGORY'],
    ];

    if (dashboardData.costsByCategory) {
      const categoryData = dashboardData.costsByCategory as Record<string, number>;
      Object.entries(categoryData).forEach(([cat, amount]) => {
        csvRows.push([getCategoryLabel(cat as CostCategory), String(amount)]);
      });
    }

    csvRows.push([]);
    csvRows.push(['DIVISION BREAKDOWN']);
    csvRows.push(['Division', 'Programs', 'Total Costs']);

    if (dashboardData.divisions) {
      dashboardData.divisions.forEach((div: DivisionCostSummary) => {
        csvRows.push([div.divisionName, div.programCount.toString(), div.totalCosts.toString()]);
      });
    }

    const csvContent = csvRows.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cost-dashboard-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!canView) {
    return (
      <div className="min-h-screen glass-page-bg p-6 lg:p-8">
        <GlassCard className="p-12 text-center">
          <DollarSign className="w-16 h-16 text-[var(--muted)] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[var(--text)] mb-2">Access Restricted</h2>
          <p className="text-[var(--muted)]">
            You do not have permission to view the cost dashboard. Contact an administrator for access.
          </p>
        </GlassCard>
      </div>
    );
  }

  if (loading.dashboard) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading cost dashboard..." />
      </div>
    );
  }

  const utilizationPercent = dashboardData?.utilizationPercent ?? 0;
  const utilizationColor: 'rose' | 'amber' | 'emerald' = utilizationPercent > 100 ? 'rose' : utilizationPercent > 80 ? 'amber' : 'emerald';

  // Calculate category totals for the chart
  const categoryTotals = (dashboardData?.costsByCategory || {}) as Record<string, number>;
  const totalCategoryAmount = Object.values(categoryTotals).reduce((sum, val) => sum + val, 0);

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      <SectionReveal>
        <GlassPageHeader
          title="Cost Dashboard"
          subtitle="Executive overview of organizational costs"
          icon={<DollarSign className="w-6 h-6" />}
          iconGradient="blue-violet"
          className="mb-6"
          actions={
            <div className="flex items-center gap-3">
              <GlassButton
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                leftIcon={<RefreshCw className={`w-4 h-4 ${loading.dashboard ? 'animate-spin' : ''}`} />}
              >
                Refresh
              </GlassButton>
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={handleExportCSV}
                disabled={!dashboardData}
                leftIcon={<Download className="w-4 h-4" />}
              >
                Export
              </GlassButton>
            </div>
          }
        />
      </SectionReveal>

      {error && (
        <div className="p-4 bg-status-error/10 border border-status-error/30 rounded-xl mb-6">
          <p className="text-status-error text-sm font-medium">{error}</p>
        </div>
      )}

      {/* Summary Metrics */}
      <SectionReveal delay={0.1}>
        <CollapsibleMetricsSection storageKey="cost-dashboard">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Costs"
              value={formatCurrencyCompact(dashboardData?.totalCosts)}
              subtitle="Year to date"
              icon={<DollarSign className="w-5 h-5" />}
              iconColor="violet"
            />
            <MetricCard
              title="Total Budget"
              value={formatCurrencyCompact(dashboardData?.totalBudget)}
              subtitle="Allocated budget"
              icon={<Building2 className="w-5 h-5" />}
              iconColor="blue"
            />
            <MetricCard
              title="Utilization"
              value={formatPercent(dashboardData?.utilizationPercent)}
              subtitle={utilizationPercent > 100 ? 'Over budget' : 'On track'}
              icon={<Percent className="w-5 h-5" />}
              iconColor={utilizationColor}
            />
            <MetricCard
              title="Divisions"
              value={dashboardData?.divisions?.length ?? 0}
              subtitle="With cost data"
              icon={<Building2 className="w-5 h-5" />}
              iconColor="blue"
            />
          </div>
        </CollapsibleMetricsSection>
      </SectionReveal>

      {/* Overall Budget Progress */}
      {dashboardData?.totalBudget && dashboardData.totalBudget > 0 && (
        <SectionReveal delay={0.15}>
          <GlassCard className="p-6 mb-6">
            <h3 className="text-lg font-semibold text-[var(--text)] mb-4">Overall Budget Utilization</h3>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-[var(--muted)]">Total Spend vs Budget</span>
              <span className="text-sm font-medium text-[var(--text)]">
                {formatCurrencyCompact(dashboardData.totalCosts)} / {formatCurrencyCompact(dashboardData.totalBudget)}
              </span>
            </div>
            <ProgressBar
              value={Math.min(utilizationPercent, 100)}
              color={utilizationColor}
              size="lg"
              gradient
            />
            {utilizationPercent > 100 && (
              <div className="mt-3 p-3 bg-status-error-bg border border-status-error/30 rounded-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-status-error" />
                <span className="text-sm text-status-error">
                  Budget exceeded by {formatCurrencyCompact(dashboardData.totalCosts - dashboardData.totalBudget)} ({formatPercent(utilizationPercent - 100)} over)
                </span>
              </div>
            )}
          </GlassCard>
        </SectionReveal>
      )}

      {/* Cost by Category */}
      <SectionReveal delay={0.2}>
        <GlassCard className="p-6 mb-6">
          <h3 className="text-lg font-semibold text-[var(--text)] mb-4">Costs by Category</h3>
          {totalCategoryAmount > 0 ? (
            <div className="space-y-4">
              {Object.entries(categoryTotals)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .map(([category, amount]) => {
                  const amountNum = amount as number;
                  const percentage = totalCategoryAmount > 0 ? (amountNum / totalCategoryAmount) * 100 : 0;
                  return (
                    <div key={category}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${getCategoryColor(category as CostCategory)}`} />
                          <span className="text-sm text-[var(--text)]">{getCategoryLabel(category as CostCategory)}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-[var(--muted)]">{percentage.toFixed(1)}%</span>
                          <span className="text-sm font-semibold text-[var(--text)]">{formatCurrencyCompact(amountNum)}</span>
                        </div>
                      </div>
                      <div className="h-2 bg-[var(--surface-hover)] rounded-full overflow-hidden">
                        <div
                          className={`h-full ${getCategoryColor(category as CostCategory)} rounded-full transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <p className="text-center text-[var(--muted)] py-8">No cost data available</p>
          )}
        </GlassCard>
      </SectionReveal>

      {/* Division Breakdown */}
      <SectionReveal delay={0.25}>
        <GlassCard className="p-6">
          <h3 className="text-lg font-semibold text-[var(--text)] mb-4">Division Cost Breakdown</h3>
          {dashboardData?.divisions && dashboardData.divisions.length > 0 ? (
            <div className="space-y-3">
              {dashboardData.divisions.map((division: DivisionCostSummary) => (
                <div key={division.divisionId} className="border border-[var(--border)] rounded-xl overflow-hidden">
                  <div
                    className="p-4 cursor-pointer hover:bg-[var(--surface-hover)] transition-colors flex items-center justify-between"
                    onClick={() => toggleDivision(division.divisionId)}
                  >
                    <div className="flex items-center gap-3">
                      {expandedDivisions.has(division.divisionId) ? (
                        <ChevronDown className="w-5 h-5 text-[var(--muted)]" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-[var(--muted)]" />
                      )}
                      <div>
                        <h4 className="font-semibold text-[var(--text)]">{division.divisionName}</h4>
                        <p className="text-xs text-[var(--muted)]">{division.programCount} programs</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-violet-600 dark:text-violet-400">
                        {formatCurrencyCompact(division.totalCosts)}
                      </p>
                    </div>
                  </div>

                  {expandedDivisions.has(division.divisionId) && division.programs && (
                    <div className="border-t border-[var(--border)] bg-[var(--surface-hover)]/50 p-4">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left">
                            <th className="pb-2 text-xs font-semibold text-[var(--muted)] uppercase">Program</th>
                            <th className="pb-2 text-right text-xs font-semibold text-[var(--muted)] uppercase">Total Costs</th>
                            <th className="pb-2 text-right text-xs font-semibold text-[var(--muted)] uppercase">Utilization</th>
                          </tr>
                        </thead>
                        <tbody>
                          {division.programs.map((program: ProgramCostSummary) => (
                            <tr key={program.programId} className="border-t border-[var(--border)]">
                              <td className="py-3 text-sm text-[var(--text)]">{program.programName}</td>
                              <td className="py-3 text-sm text-right font-medium text-[var(--text)]">
                                {formatCurrencyCompact(program.totalCosts)}
                              </td>
                              <td className="py-3 text-right">
                                {program.budgetUtilization !== null ? (
                                  <GlassBadge
                                    variant={
                                      program.budgetUtilization > 100 ? 'danger' : program.budgetUtilization > 80 ? 'warning' : 'success'
                                    }
                                    size="sm"
                                  >
                                    {formatPercent(program.budgetUtilization)}
                                  </GlassBadge>
                                ) : (
                                  <span className="text-xs text-[var(--muted)]">No budget</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-[var(--muted)] mx-auto mb-4" />
              <p className="text-[var(--muted)]">No division cost data available</p>
            </div>
          )}
        </GlassCard>
      </SectionReveal>

      {/* Quarterly Breakdown */}
      {dashboardData?.costsByQuarter && Object.keys(dashboardData.costsByQuarter).length > 0 && (
        <SectionReveal delay={0.3}>
          <GlassCard className="p-6 mt-6">
            <h3 className="text-lg font-semibold text-[var(--text)] mb-4">Quarterly Costs</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((quarter) => {
                const quarterlyData = dashboardData.costsByQuarter as Record<number, number>;
                const amount = quarterlyData[quarter] || 0;
                return (
                  <div
                    key={quarter}
                    className="p-4 rounded-xl bg-gradient-to-br from-violet-50/80 to-violet-100/40 dark:from-violet-900/20 dark:to-violet-800/10"
                  >
                    <p className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide mb-1">
                      Q{quarter}
                    </p>
                    <p className="text-2xl font-bold text-violet-600 dark:text-violet-400">
                      {formatCurrencyCompact(amount)}
                    </p>
                  </div>
                );
              })}
            </div>
          </GlassCard>
        </SectionReveal>
      )}
    </div>
  );
};

export default CostDashboardPage;
